import { MutableRefObject, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { KeyboardType, ReturnKeyTypeOptions, TextInput } from 'react-native';
import { ClearButton } from '@shared/components/Button';
import { View, ViewProps } from '@shared/components/View';
import { MBTypographyStylesRaw } from '@shared/components/typography';
import { ColorThemeContext, grid } from '@shared/styles';
import { i18n } from '@shared/util/i18n';
import { MBIconV2 } from '../image/iconV2';
import { Styles } from './styles';
import classNames from './MBSearchBar.module.css';

export interface MBSearchBarProps extends ViewProps {
  placeholder?: string;
  onChangeText(text: string): void;
  onSubmitEditing?(): void;

  value?: string;
  initialValue?: string;

  autoFocus?: boolean;
  keyboardType?: KeyboardType;
  onBlur?(): void;
  onFocus?(): void;
  onClear?(): void;
  forwardRef?: MutableRefObject<TextInput | null>;
  returnKeyType?: ReturnKeyTypeOptions;
  testID?: string;
}

export function MBSearchBar({
  forwardRef,
  value: defaultValue = '',
  style,
  autoFocus = false,
  keyboardType,
  onChangeText,
  onBlur,
  onFocus,
  onClear,
  onSubmitEditing,
  placeholder = i18n('common.searchDotDotDot'),
  returnKeyType = 'search',
  testID,
  ...otherProps
}: MBSearchBarProps) {
  const colors = useContext(ColorThemeContext);
  const textInput = useRef<TextInput>(null);
  const [value, setValue] = useState(defaultValue);
  const [focused, setFocused] = useState(autoFocus);

  if (forwardRef && textInput.current) {
    forwardRef.current = textInput.current;
  }

  const handleBlur = useCallback(() => {
    setFocused(false);
    !!onBlur && onBlur();
  }, [setFocused, onBlur]);

  const handleFocus = useCallback(() => {
    setFocused(true);
    !!onFocus && onFocus();
  }, [setFocused, onFocus]);

  const handleChangeText = useCallback(
    (updatedValue: string) => {
      setValue(updatedValue);
      onChangeText(updatedValue);
    },
    [setValue, onChangeText],
  );

  useEffect(() => setValue(defaultValue), [defaultValue]);

  // Make the text input focused whenever we press the search bar. Even if we press
  // the icon or other non-textinput area, the keyboard should appear. Focusing is
  // deferred a frame so it lands after the browser's own mousedown focus handling,
  // which would otherwise blur the input again.
  const handlePress = useCallback(() => {
    requestAnimationFrame(() => textInput.current?.focus());
  }, []);

  const handleClear = useCallback(() => {
    textInput.current?.focus();
    handleChangeText('');
    !!onClear && onClear();
  }, [textInput, handleChangeText, onClear]);

  return (
    <View
      {...otherProps}
      onPointerDown={handlePress}
      className={[classNames.searchBar, focused && classNames.focused]}
      style={style}
    >
      <View center paddingHorizontal={1.5}>
        <MBIconV2 name="magnifying-search-01" color={colors.input.label} size={grid(3)} />
      </View>
      <View flex>
        <TextInput
          ref={textInput}
          value={value}
          autoFocus={autoFocus}
          style={[
            MBTypographyStylesRaw.body1,
            Styles.textInput,
            { color: colors.input.foregroundActive },
          ]}
          onBlur={handleBlur}
          onFocus={handleFocus}
          onChangeText={handleChangeText}
          onSubmitEditing={onSubmitEditing}
          placeholder={placeholder}
          placeholderTextColor={colors.input.label}
          underlineColorAndroid="transparent"
          returnKeyType={returnKeyType}
          keyboardType={keyboardType}
          testID={testID}
        />
      </View>

      {!!value && (
        <View paddingRight={1}>
          <ClearButton onClick={handleClear} />
        </View>
      )}
    </View>
  );
}
